/* eslint-disable no-lonely-if */
import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { nth, attempt } from 'lodash';

import DataAid from '../constants/data-aids';
import { UX2, constants } from '@wsb/guac-widget-core';

const { EDIT } = constants.renderModes;
const DESC_LIMIT = 140;
const BR_LIMIT = 3;
// eslint-disable-next-line max-len
const URL_REGEX = /https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s<]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s<]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]\.[^\s<]{2,}|www\.[a-zA-Z0-9]\.[^\s<]{2,}/;
const BR_REGEX = /<br><\/\s*br>|<br\s*\/>|<br>/;
const NEWLINE_REGEX = /[\r\n\u21b5]/;
const ANCHOR_REGEX = /<a[^>]*>[^<]+<\/a>/;
const ALL_REGEX = new RegExp(`(${URL_REGEX.source}|${BR_REGEX.source}|${NEWLINE_REGEX.source}|${ANCHOR_REGEX.source})`, 'g');

class EventDescription extends Component {
  static get propTypes() {
    return {
      desc: PropTypes.string,
      smallScreen: PropTypes.bool,
      eventDetails: PropTypes.string,
      isManual: PropTypes.bool.isRequired,
      order: PropTypes.number.isRequired,
      renderMode: PropTypes.string
    };
  }

  static defaultProps = {
    isManual: false
  }

  constructor(props) {
    super(props);
    const { isManual, renderMode } = props;
    this.descBeginning = 0;
    this.descCount = 0;
    this.brCount = 0;
    this.shouldRenderExpandButton = false;
    this.onButtonClick = this.onButtonClick.bind(this);
    this.state = {
      // expanded by default for manual events in the editor to allow inline editing:
      expanded: isManual && renderMode === EDIT
    };
  }

  renderAnchor(item, defaultProps) {
    const { expanded } = this.state;
    const innerText = nth(/>(.*?)<\/a>/.exec(item), 1) || '';
    const href = nth(/\shref="(.*?)"/.exec(item), 1) || '';
    const formatUrl = _href => _href.indexOf('http') === 0 ? _href : `http://${_href}`;
    const props = Object.assign({}, defaultProps, {
      target: '_blank',
      href: href ? formatUrl(href) : '',
      children: innerText
    });
    this.descCount += innerText.length;
    if (!expanded && this.descCount > DESC_LIMIT) {
      return null;
    }
    return <UX2.Element.Link { ...props } />;
  }

  renderAnchorFromUrl(item, defaultProps) {
    const { expanded } = this.state;
    const props = Object.assign({}, defaultProps, {
      href: item.indexOf('http') === 0 ? item : `http://${item}`,
      target: '_blank'
    });
    this.descCount += item.length;
    if (!expanded && this.descCount > DESC_LIMIT) {
      return null;
    }
    return <UX2.Element.Link { ...props }/>;
  }

  renderBreakpoint() {
    const { expanded } = this.state;
    this.brCount++;
    this.descBeginning++;
    if (!expanded && this.brCount > BR_LIMIT) {
      this.descCount = DESC_LIMIT + 1;
      return null;
    }
    return <br/>;
  }

  renderDescElement(item, props) {
    const { expanded } = this.state;
    let text = item;
    if (!expanded && (this.descCount + item.length) > DESC_LIMIT) {
      text = text.substring(0, DESC_LIMIT - this.descCount);
    }
    this.descCount += item.length;
    return (
      <UX2.Element.Element { ...props } >
        { RegExp(NEWLINE_REGEX).test(item) && <br/> }
        { text }
      </UX2.Element.Element>
    );
  }

  getDescElementProps(item, index) {
    const props = {
      children: item
    };
    if (index === this.descBeginning) {
      props['data-aid'] = DataAid.CALENDAR_DESC_BEGINNING;
    }

    return props;
  }

  formatText(desc) {
    const { isManual, renderMode } = this.props;
    const { expanded } = this.state;
    if (expanded && isManual && renderMode !== EDIT) { return desc; }
    const descArr = desc.split(ALL_REGEX);

    this.descBeginning = 0; // Track in order to add data-aid to first description element
    this.descCount = 0; // Track in order to hide text that goes over the DESC_LIMIT
    this.brCount = 0; // Track in order to hide text that goes over the BR_LIMIT

    const descElements = isManual ? this.formatManualDesc(desc) : this.formatLinkedDesc(descArr);

    if (this.descCount > DESC_LIMIT) {
      if (!expanded && !isManual) {
        descElements.push(this.renderElipsis());
      }
      this.shouldRenderExpandButton = true;
    } else {
      this.shouldRenderExpandButton = false;
    }

    return descElements;
  }

  formatLinkedDesc(descArr) {
    return descArr.map((item, index) => {
      // "Replace" the strings in the array with HTML elements
      // Handle cases for anchor tags, urls, break points, and new lines
      if (!item) {
        this.descBeginning++;
        return null;
      }

      const props = this.getDescElementProps(item, index);

      if (RegExp(ANCHOR_REGEX).test(item)) {
        return this.renderAnchor(item, props);
      }
      if (RegExp(URL_REGEX).test(item)) {
        return this.renderAnchorFromUrl(item, props);
      }
      if (RegExp(`^${BR_REGEX.source}|${NEWLINE_REGEX.source}$`).test(item)) {
        return this.renderBreakpoint();
      }

      // Strings without special formatting:
      return this.renderDescElement(item, props);
    });
  }

  formatManualDesc(desc) {
    let isJSONString = true;
    const parsedDesc = attempt(() => {
      try {
        return JSON.parse(desc);
      } catch (e) {
        isJSONString = false;
        return [{ text: desc, type: 'unstyled' }];
      }
    }, [desc]);

    const descArr = isJSONString ? parsedDesc.blocks : parsedDesc;

    const formattedDesc = [];

    for (let i = 0; i < descArr.length; i++) {
      const item = descArr[i];
      const text = item.text;
      const prevDescCount = this.descCount;
      this.descCount += text.length;

      if (this.descCount > DESC_LIMIT) {
        item.text = text.substring(0, DESC_LIMIT - prevDescCount) + '...';
        formattedDesc.push(item);
        break;
      }
      formattedDesc.push(item);
    }

    if (isJSONString) {
      parsedDesc.blocks = formattedDesc;
      return JSON.stringify(parsedDesc);
    }

    return JSON.stringify({
      blocks: formattedDesc,
      entityMap: {}
    });
  }

  renderElipsis() {
    return (
      <UX2.Element.Element data-aid={ DataAid.CALENDAR_DESC_ELIPSIS }>...</UX2.Element.Element>
    );
  }

  renderExpandButton() {
    const { expanded } = this.state;
    return (
      <UX2.Element.MoreLink.Expand
        tag='span'
        expanded={ expanded }
        data-aid={ DataAid.CALENDAR_DESC_EXPAND }
        style={{
          marginTop: 'small',
          userSelect: 'none'
        }}
        onClick={ this.onButtonClick }
      >
        { this.props.eventDetails }
      </UX2.Element.MoreLink.Expand>
    );
  }

  onButtonClick() {
    this.setState({ expanded: !this.state.expanded });
  }

  render() {
    const { smallScreen, desc, isManual, order } = this.props;
    const { expanded } = this.state;
    if (!desc) return null;

    // Handle non-breaking spaces and
    // greater-than and less-than signs
    // Strip all HTML tags except breakpoints and anchors
    const text = desc
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/<(?!br|\/br|a|\/a\s*\/?)(?!a\s*\/?)[^>]+>/g, '');

    const shouldHideAll = smallScreen && !expanded;
    const routeProps = isManual ? {
      'data-field-id': 'events.desc',
      'data-field-route': `/events/${order}`
    } : {};

    return (
      <UX2.Element.Block data-aid={ DataAid.CALENDAR_DESC }>
        <UX2.Element.Text
          richtext={ isManual }
          data-aid={ DataAid.CALENDAR_DESC_TEXT }
          style={{ marginTop: 'small' }}
          { ...routeProps }
        >
          { !shouldHideAll && this.formatText(text) }
        </UX2.Element.Text>
        { smallScreen || this.shouldRenderExpandButton ? this.renderExpandButton() : null }
      </UX2.Element.Block>
    );
  }
}

export default EventDescription;
